import React from "react";
import { CCol, CFormGroup, CLabel, CRow } from "@coreui/react";
import { getFieldErrors } from "../../../models/errors";
import { FieldErrors } from "../../form/FieldErrors";
import CurrencyField from "../../currencies/CurrencyField";
import AnimatedCheckbox from "../../checkbox/AnimatedCheckbox";
import { useInvoiceFormContext } from "../context/InvoiceFormContext";

const SectionDivider: React.FC<{ icon: string; label: string }> = ({
  icon,
  label,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      marginBottom: "16px",
      marginTop: "16px",
    }}
  >
    <div
      style={{
        width: "4px",
        height: "18px",
        background: "#006657",
        borderRadius: "2px",
        marginRight: "10px",
        flexShrink: 0,
      }}
    />
    <i
      className={`fa ${icon} text-primary mr-2`}
      style={{ fontSize: "0.85rem" }}
    ></i>
    <h6
      style={{
        margin: 0,
        fontWeight: 600,
        color: "var(--theme-muted)",
        fontSize: "0.78rem",
        textTransform: "uppercase",
        letterSpacing: "0.8px",
      }}
    >
      {label}
    </h6>
    <div
      style={{
        flex: 1,
        height: "1px",
        background: "var(--theme-divider)",
        marginLeft: "10px",
      }}
    />
  </div>
);

const TotalLabel: React.FC<{
  required?: boolean;
  children: React.ReactNode;
}> = ({ required, children }) => (
  <CLabel
    className="mb-0"
    style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}
  >
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

export const InvoiceFormTotals: React.FC = () => {
  const {
    currency,
    totalAmount,
    amount10Percent,
    amount5Percent,
    amountExempt,
    iva10Percent,
    iva5Percent,
    applyDiscount,
    globalDiscount,
    onApplyDiscountChange,
    onGlobalDiscountChange,
    errors,
  } = useInvoiceFormContext();
  return (
    <>
      <SectionDivider icon="fa-calculator" label="Resumen de Totales" />
      <div
        style={{
          background: "var(--theme-highlight)",
          border: "1px solid var(--theme-highlight-border)",
          borderRadius: "8px",
          padding: "16px 20px",
          marginBottom: "16px",
        }}
      >
        {/* Total principal destacado */}
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={2}>
              <TotalLabel required>Total:</TotalLabel>
            </CCol>
            <CCol md={3}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: "var(--theme-accent)",
                }}
              >
                <CurrencyField
                  currency={currency}
                  placeholder="Total"
                  limit={1000000000000}
                  disabled={true}
                  value={totalAmount}
                />
              </div>
              <FieldErrors
                errors={getFieldErrors("amount", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>

        {/* Breakdown de bases */}
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={2}>
              <TotalLabel>Base 10%:</TotalLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={currency}
                placeholder="Total 10%"
                limit={1000000000000}
                disabled={true}
                value={amount10Percent}
              />
              <FieldErrors
                errors={getFieldErrors("amount10Percent", errors) as string[]}
              ></FieldErrors>
            </CCol>
            <CCol md={2}>
              <TotalLabel>Base 5%:</TotalLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={currency}
                placeholder="Total 5%"
                limit={1000000000000}
                disabled={true}
                value={amount5Percent}
              />
              <FieldErrors
                errors={getFieldErrors("amount5Percent", errors) as string[]}
              ></FieldErrors>
            </CCol>
            <CCol md={2}>
              <TotalLabel>Exento:</TotalLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={currency}
                placeholder="Total Exento"
                limit={1000000000000}
                disabled={true}
                value={amountExempt}
              />
              <FieldErrors
                errors={getFieldErrors("amountExempt", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>

        {/* IVA */}
        <CFormGroup className="mb-0">
          <CRow className="align-items-center">
            <CCol md={2}>
              <TotalLabel>IVA 10%:</TotalLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={currency}
                placeholder="IVA 10%"
                limit={1000000000000}
                disabled={true}
                value={iva10Percent}
              />
              <FieldErrors
                errors={getFieldErrors("iva10Percent", errors) as string[]}
              ></FieldErrors>
            </CCol>
            <CCol md={2}>
              <TotalLabel>IVA 5%:</TotalLabel>
            </CCol>
            <CCol md={2}>
              <CurrencyField
                currency={currency}
                placeholder="IVA 5%"
                limit={1000000000000}
                disabled={true}
                value={iva5Percent}
              />
              <FieldErrors
                errors={getFieldErrors("iva5Percent", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
      </div>

      {/* Descuento (fuera del box de totales) */}
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <TotalLabel>Aplicar Descuento:</TotalLabel>
          </CCol>
          <CCol md={2}>
            <CRow>
              <AnimatedCheckbox
                value={applyDiscount}
                onChange={onApplyDiscountChange}
              ></AnimatedCheckbox>
            </CRow>
            <FieldErrors
              errors={getFieldErrors("applyDiscount", errors) as string[]}
            ></FieldErrors>
          </CCol>
          {applyDiscount && (
            <>
              <CCol md={2}>
                <TotalLabel>Descuento Global:</TotalLabel>
              </CCol>
              <CCol md={2}>
                <CurrencyField
                  currency={currency}
                  placeholder="Introduzca el monto"
                  onChange={onGlobalDiscountChange}
                  value={globalDiscount}
                  limit={1000000000000}
                />
                <FieldErrors
                  errors={getFieldErrors("globalDiscount", errors) as string[]}
                ></FieldErrors>
              </CCol>
            </>
          )}
        </CRow>
      </CFormGroup>
    </>
  );
};
